import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  DEMO_CURL,
  DEMO_KEY,
  DEMO_PATH,
  DEMO_QUERY,
  DEMO_RATE,
  type DemoRate,
} from '../data/demoRate.mjs'
import { usePageMeta } from '../lib/meta'

// ── Dependency-free JSON syntax highlighter (brand tokens: cobalt keys, rate-red numbers). ──
type Tok = { v: string; c: 'key' | 'str' | 'num' | 'bool' | 'null' | 'punct' }
const TOK_CLASS: Record<Tok['c'], string> = {
  key: 'text-link',
  str: 'text-ink',
  num: 'text-signal',
  bool: 'text-up',
  null: 'text-faint',
  punct: 'text-faint',
}
function tokenizeJson(value: unknown): Tok[] {
  const json = JSON.stringify(value, null, 2)
  const re = /"(?:\\.|[^"\\])*"(\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g
  const toks: Tok[] = []
  let last = 0
  let m = re.exec(json)
  while (m !== null) {
    if (m.index > last) toks.push({ v: json.slice(last, m.index), c: 'punct' })
    const text = m[0] ?? ''
    let c: Tok['c']
    if (text.startsWith('"')) c = m[1] ? 'key' : 'str'
    else if (text === 'true' || text === 'false') c = 'bool'
    else if (text === 'null') c = 'null'
    else c = 'num'
    toks.push({ v: text, c })
    last = re.lastIndex
    m = re.exec(json)
  }
  if (last < json.length) toks.push({ v: json.slice(last), c: 'punct' })
  return toks
}

function JsonBlock({ value }: { value: unknown }) {
  const toks = tokenizeJson(value)
  return (
    <pre className="mono max-h-[520px] overflow-auto p-5 text-[12.5px] leading-[1.65]">
      <code>
        {toks.map((t, i) => (
          <span key={`${i}-${t.c}`} className={TOK_CLASS[t.c]}>
            {t.v}
          </span>
        ))}
      </code>
    </pre>
  )
}

// Each tile maps to something the real API genuinely returns (see supabase/functions/frm-api/index.ts
// and packages/db/migrations). No invented fields.
const HIGHLIGHTS: { k: string; v: string }[] = [
  { k: 'Per-lane rate', v: 'GET /lane: all-in 20GP, 40GP and 40HC for a port-pair' },
  { k: 'Representative sailing', v: 'Carrier, vessel, voyage, service mode and next ETD / ETA' },
  { k: 'Sailings behind it', v: 'GET /lane/cards: every harvested card for the lane' },
  { k: 'Daily median history', v: 'GET /lane/history: a rate curve you can chart' },
  { k: 'Trade index', v: 'GET /indices: avg, median, min and max per trade bucket' },
  { k: '{ data } envelope', v: 'One stable shape; errors are { error: { code, message } }' },
  { k: 'Usage answerback', v: 'X-Usage-* headers on every call, for every cap set' },
  { k: 'One family key', v: 'A tmcp_ key entitled to the freightrates product' },
]

const PARAMS: { name: string; example: string; note: string }[] = [
  { name: 'pol', example: 'CNSHA', note: 'Port of loading (origin) UN/LOCODE' },
  { name: 'pod', example: 'NLRTM', note: 'Port of discharge (destination) UN/LOCODE' },
]

// The real endpoints, as documented in /openapi.json.
const ENDPOINTS: { method: string; path: string; note: string }[] = [
  { method: 'GET', path: '/indices', note: 'Latest weekly rate index per trade bucket' },
  { method: 'GET', path: '/lane', note: 'Indicative rate for one port-pair (pol, pod)' },
  { method: 'GET', path: '/lane/cards', note: 'Sailing cards behind a lane (pol, pod)' },
  { method: 'GET', path: '/lane/history', note: 'Daily median rate history (pol, pod)' },
]

const API_BASE = 'https://api.freightratesmcp.com'

export function DevelopersPage() {
  usePageMeta(
    'Rates API & MCP for ocean freight — FreightRatesMCP',
    'Ocean spot-rate intelligence your code can read. A REST API for the trade rate index, per-lane indicative rates keyed by UN/LOCODE, the sailings behind each lane and daily rate history. Try it with the public demo key, no signup.',
    '/developers',
    ['en']
  )

  const [resp, setResp] = useState<DemoRate>(DEMO_RATE)
  const [ran, setRan] = useState(false)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  async function runIt() {
    setBusy(true)
    try {
      const res = await fetch(`${DEMO_PATH}?${DEMO_QUERY}`, {
        headers: { Authorization: `Bearer ${DEMO_KEY}` },
      })
      if (res.ok) {
        const json = (await res.json()) as DemoRate
        if (json?._demo) {
          setResp(json)
          setRan(true)
        }
      }
    } catch {
      // The statically-rendered sample stays on screen, so the first impression is never an error.
    } finally {
      setBusy(false)
    }
  }

  function copyCurl() {
    navigator.clipboard?.writeText(DEMO_CURL)
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }

  return (
    <div className="mx-auto max-w-6xl px-5 pb-24 pt-16">
      {/* Masthead */}
      <div className="mx-auto max-w-2xl text-center">
        <div className="mono text-xs uppercase tracking-[0.22em] text-signal">Developers</div>
        <h1 className="mt-3 text-[clamp(2.2rem,5vw,3.4rem)] font-light leading-[1.03] tracking-[-0.04em]">
          Ocean rates your <span className="hl">code</span> can read.
        </h1>
        <p className="mt-4 text-[1.05rem] leading-relaxed text-muted">
          A REST API for the trade rate index, per-lane indicative rates, the sailings behind each
          lane and daily rate history. Try it now with the public demo key. No signup, no card.
        </p>
      </div>

      {/* The demo call — request + response, always a rich 200 */}
      <div className="mx-auto mt-12 max-w-4xl">
        <div className="card overflow-hidden">
          {/* Label bar: clearly synthetic, with the endpoint */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-5 py-3">
            <span className="mono rounded-full bg-signal-soft px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-signal">
              Demo
            </span>
            <span className="text-[13px] text-muted">
              Synthetic sample, not a live quote. Always returns a rich 200.
            </span>
            <span className="mono ml-auto text-[11.5px] text-faint">
              <span className="text-up">GET</span> {DEMO_PATH}
            </span>
          </div>

          {/* Request terminal */}
          <div className="tone-dark bg-surface-0">
            <div className="flex items-center gap-2 px-5 pt-4">
              <span className="h-2.5 w-2.5 rounded-full bg-down/80" aria-hidden="true" />
              <span className="h-2.5 w-2.5 rounded-full bg-signal/70" aria-hidden="true" />
              <span className="h-2.5 w-2.5 rounded-full bg-up/70" aria-hidden="true" />
              <span className="mono ml-2 text-[11px] uppercase tracking-wider text-faint">
                Request
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={copyCurl}
                  className="mono rounded-md border border-border px-2.5 py-1 text-[11px] text-muted transition-colors hover:border-signal hover:text-signal"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <button
                  type="button"
                  onClick={runIt}
                  disabled={busy}
                  className="btn-accent mono rounded-md px-3 py-1 text-[11px] font-semibold disabled:opacity-70"
                >
                  {busy ? 'Running' : 'Run it'}
                </button>
              </div>
            </div>
            <pre className="mono overflow-auto px-5 pb-5 pt-3 text-[12.5px] leading-[1.6] text-ink">
              <code>
                <span className="text-signal">curl</span> -s{' '}
                <span className="text-up">
                  '{`https://freightratesmcp.com${DEMO_PATH}?${DEMO_QUERY}`}'
                </span>{' '}
                \{'\n  '}-H <span className="text-up">'Authorization: Bearer {DEMO_KEY}'</span>
              </code>
            </pre>
          </div>

          {/* Response terminal */}
          <div className="tone-dark border-t border-border bg-void">
            <div className="flex items-center gap-2 px-5 pt-4">
              <span className="mono text-[11px] uppercase tracking-wider text-faint">Response</span>
              <span className="mono rounded-full border border-up/40 px-2 py-0.5 text-[10px] font-semibold text-up">
                200 OK
              </span>
              {ran && (
                <span className="mono text-[10.5px] text-faint">
                  verified live from {DEMO_PATH}
                </span>
              )}
            </div>
            <JsonBlock value={resp} />
          </div>

          {/* CTA on the response */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-border px-5 py-4">
            <p className="text-[13.5px] text-muted">
              This is the public demo key. Get your live rates key to pull real lanes.
            </p>
            <div className="ml-auto flex items-center gap-3">
              <Link to="/account" className="text-[13.5px] font-medium text-link hover:underline">
                Create a free account
              </Link>
              <Link
                to="/pricing"
                className="btn-accent rounded-lg px-4 py-2 text-[13.5px] font-semibold"
              >
                Get your key →
              </Link>
            </div>
          </div>
        </div>

        {/* Honesty footnote */}
        <p className="serif mx-auto mt-4 max-w-2xl text-center text-[13px] italic text-muted">
          The demo endpoint returns a fixed, synthetic Shanghai to Rotterdam sample so this page can
          never greet you with an error. Live rates are free to browse on the site. The programmatic
          rates API and hosted MCP server are on Pro.
        </p>
      </div>

      {/* What the API returns */}
      <div className="mx-auto mt-20 max-w-5xl">
        <h2 className="text-center text-2xl font-semibold tracking-tight">
          The whole lane, one shape.
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-relaxed text-muted">
          Not a single number. A handful of endpoints return the shape of the lane: where it trades,
          how it moves, who runs it and how the rate has trended.
        </p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {HIGHLIGHTS.map((h) => (
            <div key={h.k} className="card p-4">
              <div className="mono text-[11px] uppercase tracking-wide text-signal">{h.k}</div>
              <div className="mt-1.5 text-[13.5px] leading-snug text-muted">{h.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Endpoint reference */}
      <div className="mx-auto mt-20 max-w-3xl">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-semibold tracking-tight">Endpoints</h2>
          <span className="mono rounded-full border border-border-strong px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-faint">
            Bearer key
          </span>
          <span className="mono ml-auto text-[12px] text-faint">{API_BASE}</span>
        </div>

        {/* The real routes, mirroring /openapi.json */}
        <div className="card mt-5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13.5px]">
              <thead>
                <tr className="mono text-[11px] uppercase tracking-wide text-faint">
                  <th className="px-5 py-2.5 font-medium">Method</th>
                  <th className="px-5 py-2.5 font-medium">Path</th>
                  <th className="px-5 py-2.5 font-medium">Returns</th>
                </tr>
              </thead>
              <tbody className="text-muted">
                {ENDPOINTS.map((e) => (
                  <tr key={e.path} className="border-t border-border">
                    <td className="px-5 py-2.5">
                      <span className="mono rounded bg-signal-soft px-2 py-0.5 text-[11px] font-semibold text-signal">
                        {e.method}
                      </span>
                    </td>
                    <td className="mono px-5 py-2.5 text-ink">{e.path}</td>
                    <td className="px-5 py-2.5">{e.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lane query params */}
        <div className="card mt-4 overflow-hidden">
          <div className="mono flex items-center gap-3 border-b border-border px-5 py-3 text-[13px]">
            <span className="rounded bg-signal-soft px-2 py-0.5 text-[11px] font-semibold text-signal">
              GET
            </span>
            <span className="text-ink">/lane</span>
            <span className="text-faint">?pol=CNSHA&amp;pod=NLRTM</span>
            <span className="ml-auto text-[11px] text-faint">query params</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13.5px]">
              <thead>
                <tr className="mono text-[11px] uppercase tracking-wide text-faint">
                  <th className="px-5 py-2.5 font-medium">Param</th>
                  <th className="px-5 py-2.5 font-medium">Example</th>
                  <th className="px-5 py-2.5 font-medium">Meaning</th>
                </tr>
              </thead>
              <tbody className="text-muted">
                {PARAMS.map((p) => (
                  <tr key={p.name} className="border-t border-border">
                    <td className="mono px-5 py-2.5 text-link">{p.name}</td>
                    <td className="mono px-5 py-2.5 text-ink">{p.example}</td>
                    <td className="px-5 py-2.5">{p.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-muted">
          Full request and response schemas, error codes and the X-Usage headers are in the{' '}
          <a
            href="/openapi.json"
            className="font-medium text-link hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            OpenAPI 3.1 spec
          </a>{' '}
          you can import into Postman, Insomnia or an SDK generator.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-muted">
          Prefer agents? The same intelligence ships as a hosted MCP server, so a model can pull an
          ocean rate as a tool call. Both the live REST API and the MCP server roll out on Pro. See{' '}
          <Link to="/pricing" className="font-medium text-link hover:underline">
            pricing
          </Link>{' '}
          for plans, or{' '}
          <Link to="/book" className="font-medium text-link hover:underline">
            talk to us
          </Link>{' '}
          about volume access.
        </p>
      </div>
    </div>
  )
}
